import * as React from "react";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request } from "../../../../../services/cpln";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { toSelectOptions } from "../../../../../services/defaults";
import { CpSwitch } from "../../../../gvc/testNewForm/CpSwitch";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { EllipsisOutlined } from "@ant-design/icons";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";
import { DefaultOptionType } from "antd/es/select";

interface Props {
  index: number;
}
export const GcpNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const [recommendedImages, setRecommendedImages] = React.useState<string[]>([]);
  const [zoneOptions, setZoneOptions] = React.useState<DefaultOptionType[]>([]);

  React.useEffect(() => {
    fetchRecommendedOptions();
  }, []);

  async function discoverZoneOptions() {
    try {
      const form = store.getState().form.instances[formId];
      const values: Mk8sInput = form.values as any;

      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "zones",
          cluster: inputToMk8s(values),
        },
      });
      const options = data.map((item: any) => ({ label: item.name, value: item.name }));
      setZoneOptions(options);
    } catch (e) {
      setZoneOptions([]);
    }
  }

  async function fetchRecommendedOptions() {
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const images: string[] = schemaRes.keys.spec.keys.provider.keys.gcp.keys.image.keys.recommended.allow;
      setRecommendedImages(images);
    } catch (e) {
      setRecommendedImages([]);
    }
  }

  const name = useCpFormValue(`gcp.nodePools.${index}.name`);
  const imageType = useCpFormValue(`gcp.nodePools.${index}.overrideImage.type`);
  const zone = useCpFormValue(`gcp.nodePools.${index}.zone`);
  const subnet = useCpFormValue(`gcp.nodePools.${index}.subnet`);
  const machineType = useCpFormValue(`gcp.nodePools.${index}.machineType`);
  const gcpRegion = useCpFormValue(`gcp.region`);

  // Re-fetch zone options when region changes
  React.useEffect(() => {
    discoverZoneOptions();
  }, [gcpRegion]);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={["gcp.nodePools"]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`gcp.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`gcp.nodePools.${index}.name`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`gcp.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`gcp.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`gcp.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`gcp.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`gcp.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray names={[`gcp.nodePools.${index}.minSize`, `gcp.nodePools.${index}.maxSize`]} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name="skip">Override Image</CpLabel>
                <CpRadioGroup
                  name={`gcp.nodePools.${index}.overrideImage.type`}
                  style={{ width: 600 }}
                  options={[
                    { label: "None", value: "none" },
                    { label: "Exact", value: "exact" },
                    { label: "Recommended", value: "recommended" },
                    { label: "Family", value: "family" },
                  ]}
                />
                {imageType === "exact" ? (
                  <>
                    <CpInput name={`gcp.nodePools.${index}.overrideImage.exact`} style={{ width: 600 }} />
                    <CpErrorWarning name={`gcp.nodePools.${index}.overrideImage.exact`} />
                  </>
                ) : imageType === "recommended" ? (
                  <>
                    <CpSelect
                      name={`gcp.nodePools.${index}.overrideImage.recommended`}
                      style={{ width: 600 }}
                      options={toSelectOptions(recommendedImages)}
                    />
                    <CpErrorWarning name={`gcp.nodePools.${index}.overrideImage.recommended`} />
                  </>
                ) : imageType === "family" ? (
                  <>
                    <div className="flex flex-col mb-4">
                      <CpLabel name={`gcp.nodePools.${index}.overrideImage.family.project`}>Project</CpLabel>
                      <CpInput name={`gcp.nodePools.${index}.overrideImage.family.project`} style={{ width: 600 }} />
                      <CpErrorWarning name={`gcp.nodePools.${index}.overrideImage.family.project`} />
                    </div>
                    <div className="flex flex-col mb-4">
                      <CpLabel name={`gcp.nodePools.${index}.overrideImage.family.family`}>Family</CpLabel>
                      <CpInput name={`gcp.nodePools.${index}.overrideImage.family.family`} style={{ width: 600 }} />
                      <CpErrorWarning name={`gcp.nodePools.${index}.overrideImage.family.family`} />
                    </div>
                  </>
                ) : (
                  <>
                    <CpErrorWarning name={`gcp.nodePools.${index}.overrideImage.type`} />
                  </>
                )}
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.zone`}>Zone</CpLabel>
                {zoneOptions.length > 0 ? (
                  <CpSelect name={`gcp.nodePools.${index}.zone`} options={zoneOptions} style={{ maxWidth: 600 }} />
                ) : (
                  <Space.Compact>
                    <CpInput name={`gcp.nodePools.${index}.zone`} style={{ maxWidth: 600 }} />
                    <Tooltip title={`Browse`}>
                      <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("zones")} />
                    </Tooltip>
                  </Space.Compact>
                )}
                <CpErrorWarning name={`gcp.nodePools.${index}.zone`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.machineType`}>Machine Type</CpLabel>
                <Space.Compact>
                  <CpInput name={`gcp.nodePools.${index}.machineType`} style={{ maxWidth: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("machineTypes")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`gcp.nodePools.${index}.machineType`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.subnet`}>Subnet</CpLabel>
                <Space.Compact>
                  <CpInput name={`gcp.nodePools.${index}.subnet`} style={{ maxWidth: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("subnets")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`gcp.nodePools.${index}.subnet`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.localPersistentDisks`}>Local Persistent Disks</CpLabel>
                <CpInputNumber name={`gcp.nodePools.${index}.localPersistentDisks`} />
                <CpErrorWarning name={`gcp.nodePools.${index}.localPersistentDisks`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.nodePools.${index}.bootDiskSize`}>Boot Disk Size</CpLabel>
                <CpInputNumber name={`gcp.nodePools.${index}.bootDiskSize`} placeholder="20" addonAfter={`GB`} />
                <CpErrorWarning name={`gcp.nodePools.${index}.bootDiskSize`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`gcp.nodePools.${index}.assignPublicIP`}>Assign Public IP</CpLabel>
                  <CpSwitch name={`gcp.nodePools.${index}.assignPublicIP`} />
                </div>
                <CpErrorWarning name={`gcp.nodePools.${index}.assignPublicIP`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`gcp.nodePools.${index}.preemptible`}>Preemptible</CpLabel>
                  <CpSwitch name={`gcp.nodePools.${index}.preemptible`} />
                </div>
                <CpErrorWarning name={`gcp.nodePools.${index}.preemptible`} />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`gcp.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`gcp.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`gcp.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "machineTypes" ? (
        <NGProviderBrowser
          title="Browse GCP Machine Types"
          initialValue={machineType}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "machineTypes",
                cluster: inputToMk8s(values),
              },
            });
            // Filter by selected zone if one is selected (keep items without zones field)
            if (zone) {
              return data.filter((item: any) => !item.zones || item.zones.includes(zone));
            }
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `gcp.nodePools.${index}.machineType`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          distinctFilters={["CPU Count", "Memory", "Arch", "GPU Type", "GPU Count"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          initialSort={["Cost per Month", "asc"]}
          labels={[
            "Name",
            "Cost per Month",
            "CPU Count",
            "Memory",
            "Arch",
            "Max Local SSDs",
            "Max Persistent Disks",
            "GPU Type",
            "GPU Count",
            "Zones",
          ]}
          sortable
          getData={(item: any) => {
            let gpuType = "";
            let gpuCount = "";
            if (
              item.accelerators &&
              Array.isArray(item.accelerators) &&
              item.accelerators.length > 0 &&
              item.accelerators[0].guestAcceleratorType
            ) {
              gpuType = item.accelerators[0].guestAcceleratorType;
            }
            if (
              item.accelerators &&
              Array.isArray(item.accelerators) &&
              item.accelerators.length > 0 &&
              item.accelerators[0].guestAcceleratorCount
            ) {
              gpuCount = item.accelerators[0].guestAcceleratorCount;
            }

            const monthlyPrice = item.monthlyPrice != null ? `$${item.monthlyPrice.toFixed(2)}` : "-";

            return [
              item.name,
              item.name,
              [item.monthlyPrice ?? 0, monthlyPrice],
              item.guestCpus,
              [item.memoryMb, formatSizeForProviderBrowser(item.memoryMb, "MB")],
              [item.architecture, item.architecture || "-"],
              [item.maxLocalSsds, `${item.maxLocalSsds || "-"}`],
              [item.maximumPersistentDisks, `${item.maximumPersistentDisks || "-"}`],
              [gpuType, gpuType || "-"],
              [gpuCount, gpuCount || "-"],
              item.zones.map((x: string) => x.split("-").at(-1)).join(", "),
            ];
          }}
        />
      ) : null}
      {browserKey === "zones" ? (
        <NGProviderBrowser
          width={1550}
          title="Browse GCP Zones"
          initialValue={zone}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: `zones`,
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `gcp.nodePools.${index}.zone`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          rightAlign={["Cost per Month"]}
          labels={["Name", "Region", "Available CPU Platforms"]}
          sortable
          getData={(item: any) => {
            return [item.name, item.name, item.region.split(`/regions/`)[1], item.availableCpuPlatforms.join(",")];
          }}
        />
      ) : null}
      {browserKey === "subnets" ? (
        <NGProviderBrowser
          title="Browse GCP Subnets"
          initialValue={subnet}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subnets",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `gcp.nodePools.${index}.subnet`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Region", "IP CIDR Range"]}
          getData={(item: any) => {
            return [item.name, item.name, item.region.split(`/regions/`)[1], item.ipCidrRange];
          }}
        />
      ) : null}
    </>
  );
};
